/**
 * Shared inspection-type display + picker helpers.
 *
 * Customer simplification (Jun 2026): "Green Light" and "Tenant Turnover" are
 * NOT separately pickable inspection types — they fold into "Move-In" and
 * "Move-Out" respectively for DISPLAY and in the create-inspection /
 * create-template pickers. The underlying enum values are unchanged on the
 * wire: backend auto-triggers + the legal move-out 21-day clock still use
 * GREEN_LIGHT / TURNOVER internally.
 *
 *   Move-Out ← MOVE_OUT + TURNOVER     (turnover/make-ready = a move-out inspection)
 *
 * GREEN_LIGHT was folded into "Move-In" by that same simplification and is NO
 * LONGER — spec 99 un-retired it and gave it back its own name. It stays out of
 * the create pickers; only its display label changed back. See the case below.
 *
 * Single source of truth — every page renders labels via `inspectionTypeLabel`
 * and offers creation choices via `PICKABLE_INSPECTION_TYPES`. List pages that
 * filter client-side by a merged value use `inspectionTypeFilterEnums` so
 * picking "Move-In" still surfaces the auto-created green-light rows.
 *
 * i18n (spec 91 R3): the DISPLAY label is resolved via the i18n SINGLETON —
 * `i18n.t('common:inspectionType.…', { defaultValue })` — because this is a
 * plain function called both inside and outside React render (list transforms,
 * headers, pickers). `defaultValue` is byte-identical to the historical
 * English, so `en` output is unchanged and an unknown/missing key still falls
 * back to the humanized English. The folding logic (GREEN_LIGHT → Move-In,
 * TURNOVER → Move-Out, legacy aliases) is untouched — only the strings change.
 */

import i18n from '../i18n';

/**
 * Resolve the human-readable label for an inspection type. Folds the retired
 * pickable values into their parent label (GREEN_LIGHT → Move-In, TURNOVER →
 * Move-Out) and keeps the legacy aliases (ANNUAL_BUILDING / ANNUAL / PERIODIC)
 * reading correctly for historical rows. Unknown values are title-cased.
 */
export function inspectionTypeLabel(type: string | null | undefined): string {
  if (!type) return i18n.t('common:inspectionType.fallback', { defaultValue: 'Inspection' });
  switch (type) {
    case 'MOVE_IN':
      return i18n.t('common:inspectionType.MOVE_IN', { defaultValue: 'Move-In' });
    /**
     * ★ A GREEN LIGHT IS NOT A MOVE-IN, AND CALLING IT ONE SENDS PEOPLE TO THE
     * WRONG SCREEN.
     *
     * Spec 75 folded this label into "Move-In" in Jun 2026, when green lights
     * were being retired from the UI altogether. Spec 99 un-retired them four
     * weeks later — the walk screen and every make-ready surface have said
     * "Green Light" ever since — but this map was never brought along, so the
     * two disagreed. PO report, 2026-08-31: *"When I press on Move In, it opens
     * a green light checklist. Why?"* — it opened one because the row WAS the
     * green light; only its label said otherwise.
     *
     * Nothing is lost by splitting them: `GREEN_LIGHT` is not offered in any
     * create picker (spec 99 kept it out), so every row carrying it is a
     * make-ready green light, which is exactly what the label now says. The
     * merged FILTER is untouched — `inspectionTypeFilterEnums` still surfaces
     * green-light rows under Move-In for anyone filtering that way.
     */
    case 'GREEN_LIGHT':
      return i18n.t('common:inspectionType.GREEN_LIGHT', { defaultValue: 'Green Light' });
    case 'MOVE_OUT':
    case 'TURNOVER':
      return i18n.t(`common:inspectionType.${type}`, { defaultValue: 'Move-Out' });
    case 'PRE_MOVE_OUT':
      return i18n.t('common:inspectionType.PRE_MOVE_OUT', { defaultValue: 'Pre-Move-Out' });
    case 'CURB_TO_ROOF':
    case 'ANNUAL_BUILDING':
      return i18n.t(`common:inspectionType.${type}`, { defaultValue: 'Curb-to-Roof' });
    case 'COMPLIANCE':
      return i18n.t('common:inspectionType.COMPLIANCE', { defaultValue: 'Compliance' });
    case 'HABITABILITY':
      return i18n.t('common:inspectionType.HABITABILITY', { defaultValue: 'Habitability' });
    case 'ANNUAL':
    case 'PERIODIC':
      return i18n.t(`common:inspectionType.${type}`, { defaultValue: 'Periodic' });
    default:
      return type
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/**
 * The single "what to call this inspection" resolver — mirrors the backend
 * `inspectionNotifyLabel(name, type, customCategoryName)` precedence EXACTLY so
 * every client surface (WO spawn title/description, list cards, headers) labels
 * a custom-category inspection by its category ("Pool & Spa") instead of the
 * generic humanized type ("Custom"):
 *   inspection.name → custom category name → humanized type.
 * Use this anywhere a client builds a label from an inspection.
 */
export function inspectionDisplayName(inspection: {
  name?: string | null;
  type?: string | null;
  customCategoryName?: string | null;
}): string {
  return (
    inspection.name?.trim() ||
    inspection.customCategoryName?.trim() ||
    inspectionTypeLabel(inspection.type)
  );
}

/** The pickable inspection-type VALUES (order = most common first). */
const PICKABLE_INSPECTION_TYPE_VALUES = [
  'MOVE_IN',
  'MOVE_OUT',
  'PRE_MOVE_OUT',
  'CURB_TO_ROOF',
  'COMPLIANCE',
  'HABITABILITY',
] as const;

/**
 * The inspection types a PM may pick when creating an inspection or template,
 * with hardcoded ENGLISH labels. Retained for the P3-deferred template/recurring
 * pages that are still fully English — a locale-reactive list would make those
 * pages half-Spanish. Core surfaces (the Inspections list) use
 * `getPickableInspectionTypes()` instead. Excludes the folded values
 * (GREEN_LIGHT, TURNOVER) and retired legacy values.
 */
export const PICKABLE_INSPECTION_TYPES: { value: string; label: string }[] = [
  { value: 'MOVE_IN', label: 'Move-In' },
  { value: 'MOVE_OUT', label: 'Move-Out' },
  { value: 'PRE_MOVE_OUT', label: 'Pre-Move-Out' },
  { value: 'CURB_TO_ROOF', label: 'Curb-to-Roof' },
  { value: 'COMPLIANCE', label: 'Compliance' },
  { value: 'HABITABILITY', label: 'Habitability' },
];

/**
 * Locale-reactive variant of `PICKABLE_INSPECTION_TYPES` — a FUNCTION (not a
 * module constant), so labels resolve to the active locale at call time via
 * `inspectionTypeLabel`. Use this on translated (core) surfaces; en output is
 * byte-identical to `PICKABLE_INSPECTION_TYPES`.
 */
export function getPickableInspectionTypes(): { value: string; label: string }[] {
  return PICKABLE_INSPECTION_TYPE_VALUES.map((value) => ({
    value,
    label: inspectionTypeLabel(value),
  }));
}

/**
 * Build the merged option list for an inspection-kind `<Select>` — the built-in
 * `PICKABLE_INSPECTION_TYPES` PLUS the org's active custom categories, rendered
 * as a SEPARATE "Custom" group so the two never blur together. Custom-category
 * option values are prefixed (`cat:<id>`) via `customCategoryOptionValue` so the
 * caller can route the choice (submit `customCategoryId` vs `type`).
 *
 * Deliberately does NOT mutate `PICKABLE_INSPECTION_TYPES` (the spec-75 built-in
 * picker stays untouched) — it composes it into AntD grouped options.
 */
export function buildInspectionKindOptions(
  categories: { id: string; name: string }[],
  customOptionValue: (id: string) => string,
): Array<{
  label: string;
  options: { value: string; label: string }[];
}> {
  const groups: Array<{ label: string; options: { value: string; label: string }[] }> = [
    {
      label: i18n.t('common:inspectionKindGroup.builtin', { defaultValue: 'Built-in types' }),
      options: getPickableInspectionTypes(),
    },
  ];
  if (categories.length > 0) {
    groups.push({
      label: i18n.t('common:inspectionKindGroup.custom', { defaultValue: 'Custom categories' }),
      options: categories.map((c) => ({
        value: customOptionValue(c.id),
        label: c.name,
      })),
    });
  }
  return groups;
}

/**
 * Map a merged picker value to the underlying enum values it should match when
 * FILTERING a list client-side. Picking "Move-In" must also surface the
 * auto-created GREEN_LIGHT inspections; "Move-Out" must also surface TURNOVER.
 * Everything else matches only itself.
 */
export function inspectionTypeFilterEnums(label: string): string[] {
  switch (label) {
    case 'MOVE_IN':
      return ['MOVE_IN', 'GREEN_LIGHT'];
    case 'MOVE_OUT':
      return ['MOVE_OUT', 'TURNOVER'];
    default:
      return [label];
  }
}

/**
 * The inspection types that cannot be walked without a unit.
 *
 * A move-in, a pre-move-out, a move-out and a green-light (final move-in
 * readiness) walk are all statements about ONE tenancy in ONE unit — the
 * move-out walk is the document a deposit deduction is argued from, the
 * pre-move-out is the warning that precedes it, and the green light is what
 * releases that unit to a new resident. A property-wide row of any of the four
 * is meaningless, so the create form asks for the unit instead of accepting a
 * walk that can never be attributed.
 *
 * Includes the folded aliases (GREEN_LIGHT reads as "Move-In", TURNOVER as
 * "Move-Out") so the rule follows the LABEL the user picked, not the spelling
 * that reaches the wire.
 *
 * Mirrored in the mobile app by `inspectionTypeRequiresUnit` in
 * `mobile/admin/lib/features/more/inspection_enum_labels.dart` — the two lists
 * are pinned to each other by `inspectionUnitRequired.test.ts`.
 */
export const UNIT_REQUIRED_INSPECTION_TYPES = [
  'MOVE_IN',
  'GREEN_LIGHT',
  'PRE_MOVE_OUT',
  'MOVE_OUT',
  'TURNOVER',
] as const;

/**
 * Does this inspection kind require a unit before it can be created?
 *
 * Accepts the create modal's merged `kind` value, so a custom category
 * (`cat:<uuid>`) answers `false` — a custom category is building-axis and may
 * legitimately be property-wide.
 */
export function inspectionTypeRequiresUnit(type: string | null | undefined): boolean {
  if (!type) return false;
  return (UNIT_REQUIRED_INSPECTION_TYPES as readonly string[]).includes(type);
}
